import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, Users, FileText, Calendar, Bell, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useUserRoles } from "@/hooks/useUserRoles";
import { cn } from "@/lib/utils";

interface QuickAction {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  color: string;
  badge?: {
    text: string;
    variant: 'default' | 'destructive' | 'outline' | 'secondary';
  };
  adminOnly?: boolean;
  guardOnly?: boolean;
}

export const QuickActionsHub = () => {
  const navigate = useNavigate();
  const { isAdmin, hasRole } = useUserRoles();
  const isGuard = hasRole ? hasRole('guard') : false;

  const quickActions: QuickAction[] = [
    {
      title: "Add New Guard",
      description: "Register a new security guard",
      icon: Plus,
      path: "/guards",
      color: "bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border-emerald-500/20 hover:from-emerald-500/20 hover:to-emerald-600/20 backdrop-blur-md",
      adminOnly: true,
    },
    {
      title: "Today's Schedule",
      description: "View and manage today's shifts",
      icon: Calendar,
      path: "/scheduling",
      color: "bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-blue-500/20 hover:from-blue-500/20 hover:to-blue-600/20 backdrop-blur-md",
    },
    {
      title: "Submit Report",
      description: "Create a new incident report",
      icon: FileText,
      path: "/reports",
      color: "bg-gradient-to-br from-purple-500/10 to-purple-600/10 border-purple-500/20 hover:from-purple-500/20 hover:to-purple-600/20 backdrop-blur-md",
      badge: {
        text: "Quick",
        variant: "secondary",
      },
    },
    {
      title: "Emergency Alerts",
      description: "View critical security alerts",
      icon: Bell,
      path: "/alerts",
      color: "bg-gradient-to-br from-red-500/10 to-red-600/10 border-red-500/20 hover:from-red-500/20 hover:to-red-600/20 backdrop-blur-md",
      badge: {
        text: "3 New",
        variant: "destructive",
      },
      adminOnly: true,
    },
  ];

  const filteredActions = quickActions.filter(action => {
    if (action.adminOnly && !isAdmin) return false;
    if (action.guardOnly && !isGuard) return false;
    return true;
  });

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-foreground">
          Quick Actions
        </h2>
        <Badge variant="outline" className="text-xs">
          Most Used
        </Badge>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {filteredActions.map((action, index) => (
          <Card
            key={action.title}
            className={cn(
              "relative overflow-hidden cursor-pointer group transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl border-2 p-4 rounded-carplay bg-card/50 backdrop-blur-xl",
              action.color
            )}
            onClick={() => navigate(action.path)}
            style={{
              animationDelay: `${index * 100}ms`
            }}
          >
            {/* CarPlay-style shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
            
            <div className="relative space-y-3">
              <div className="flex items-start justify-between">
                <div className="p-2 rounded-xl bg-background/20 backdrop-blur-sm border border-white/10 shadow-inner">
                  <action.icon className="w-5 h-5 text-foreground" />
                </div>
                {action.badge && (
                  <Badge variant={action.badge.variant} className="text-xs">
                    {action.badge.text}
                  </Badge>
                )}
              </div>
              
              <div>
                <h3 className="font-semibold text-foreground text-sm leading-tight">
                  {action.title}
                </h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {action.description}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};